import { ClassNames, useTheme } from "@emotion/react";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { darken } from "@mui/material";
import {
  DateView,
  DesktopDatePicker,
  pickersInputBaseClasses,
  pickersOutlinedInputClasses,
} from "@mui/x-date-pickers";
import { format, isValid, parseISO } from "date-fns";
import { forwardRef, useState, JSX } from "react";

import { assertNever } from "@rewards-web/shared/lib/assert-never";
import {
  ControlledFormFieldProps,
  StylableProps,
} from "@rewards-web/shared/types";

import { outlinedInputLabelStyles } from "../../style/outlined-input-label-styles";
import { useFormControlContext } from "../form/form-control";
import { DateFieldProvider } from "./provider";

const DATEONLY_FORMAT = "yyyy-MM-dd";
const MONTHONLY_FORMAT = "yyyy-MM";

export function formatDateOnly(date: Date): string {
  return format(date, DATEONLY_FORMAT);
}

export function formatMonthOnly(date: Date): string {
  return format(date, MONTHONLY_FORMAT);
}

export interface DateFieldProps
  extends StylableProps, ControlledFormFieldProps {
  label: string;
  variant?: "date" | "month";
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  readOnly?: boolean;
  hideHelperText?: boolean;
  greyReadOnly?: boolean;
  dontDisplayDayOfWeek?: boolean;
  height?: number;
  /**
   * @default false
   *
   * When true, displays the date as `yyyy-MM-dd` instead of a long-form date.
   */
  isoDisplayFormat?: boolean;
  /**
   * @default false
   *
   * By default, MUI component labels are placed within the input when the input is empty.
   * This overrides that behaviour to have it stay at the top.
   */
  keepLabelAtTop?: boolean;
}

interface DateFieldViews {
  views: DateView[];
  openTo: DateView;
}

function getViews(
  variant: NonNullable<DateFieldProps["variant"]>
): DateFieldViews {
  switch (variant) {
    case "date":
      return { views: ["year", "month", "day"], openTo: "day" };
    case "month":
      // month is the last view, so picking a month closes the picker
      return { views: ["year", "month"], openTo: "month" };
    default:
      assertNever(variant);
  }
}

function getDisplayFormat({
  variant,
  isoDisplayFormat,
  dontDisplayDayOfWeek,
}: Required<
  Pick<DateFieldProps, "variant" | "isoDisplayFormat" | "dontDisplayDayOfWeek">
>): string {
  switch (variant) {
    case "date":
      if (isoDisplayFormat) {
        return DATEONLY_FORMAT;
      }

      return dontDisplayDayOfWeek ? "PPP" : "PPPP";
    case "month":
      return "MMMM yyyy";
    default:
      assertNever(variant);
  }
}

function CalendarIcon() {
  const theme = useTheme();

  return (
    <FontAwesomeIcon
      icon={faCalendar}
      color={theme.palette.grey[800]}
      // sized like the input text
      style={{ fontSize: "1rem" }}
    />
  );
}

export const DateField = forwardRef(
  (
    {
      className,
      label,
      error,
      name,
      value,
      onChange,
      onBlur,
      variant = "date",
      minDate,
      maxDate,
      disabled,
      readOnly: readOnlyProp = false,
      hideHelperText,
      greyReadOnly,
      dontDisplayDayOfWeek = false,
      height,
      keepLabelAtTop = false,
      isoDisplayFormat = false,
    }: DateFieldProps,
    ref: any
  ): JSX.Element => {
    const { submitting, readOnly: contextReadOnly } = useFormControlContext();
    const readOnly = contextReadOnly || readOnlyProp;
    const pickerDisabled = Boolean(disabled || submitting || readOnly);
    const [open, setOpen] = useState(false);
    const { views, openTo } = getViews(variant);

    return (
      <DateFieldProvider>
        <ClassNames>
          {({ css, theme }) => (
            <DesktopDatePicker
              inputRef={ref}
              name={name}
              className={className}
              label={label}
              views={views}
              openTo={openTo}
              format={getDisplayFormat({
                variant,
                isoDisplayFormat,
                dontDisplayDayOfWeek,
              })}
              minDate={minDate}
              maxDate={maxDate}
              closeOnSelect
              open={open}
              onOpen={() => setOpen(true)}
              onClose={() => setOpen(false)}
              readOnly={readOnly}
              disabled={pickerDisabled}
              value={value ? parseISO(value) : null}
              // onAccept, not onChange: picking only a year or month fires onChange,
              // which would save a half-picked date (e.g. the year with today's month and day)
              onAccept={(date) => {
                onChange?.(
                  (() => {
                    if (!date || !isValid(date)) {
                      return null;
                    }

                    switch (variant) {
                      case "date":
                        return formatDateOnly(date);
                      case "month":
                        return formatMonthOnly(date);
                      default:
                        assertNever(variant);
                    }
                  })()
                );
              }}
              slots={{ openPickerIcon: CalendarIcon }}
              slotProps={{
                openPickerButton: { size: "small" },
                // the field is not typed into: clicking anywhere on it opens the calendar
                field: {
                  readOnly: true,
                  // on mouse down, not click: the field re-renders its text on mouse down,
                  // so a click on an empty field never arrives
                  onMouseDown: (event: React.MouseEvent<HTMLElement>) => {
                    if (
                      pickerDisabled ||
                      event.button !== 0 ||
                      (event.target as HTMLElement).closest("button")
                    ) {
                      return;
                    }

                    setOpen(true);
                  },
                },
                textField: {
                  fullWidth: true,
                  onBlur,
                  error: Boolean(error),
                  // ensure helper text stays rendered so height doesn't jump around (unless hideHelperText is true):
                  helperText: error?.message || (hideHelperText ? "" : " "),
                  InputLabelProps: {
                    ...(keepLabelAtTop ? { shrink: true } : {}),
                    // same size as the input text, which the outline notch is sized from
                    className: css`
                      ${outlinedInputLabelStyles}
                    `,
                  },
                  InputProps: {
                    className: css`
                      // 16px text with no letter-spacing, not the app's 18px body1
                      font-size: ${theme.typography.pxToRem(16)};
                      letter-spacing: normal;
                      background-color: ${theme.palette.background.paper};
                      border-radius: ${theme.spacing(1)};
                      ${
                        // an empty field shows nothing, not the format (EEEE, MMMM DD, YYYY)
                        !value &&
                        css`
                          .${pickersInputBaseClasses.sectionsContainer} {
                            opacity: 0;
                          }
                        `
                      }
                      ${
                        height &&
                        css`
                          height: ${height}px;
                        `
                      }

                      &:not(.${pickersOutlinedInputClasses.focused}):not(
                          .${pickersOutlinedInputClasses.error}
                        )
                        .${pickersOutlinedInputClasses.notchedOutline} {
                        border-color: ${theme.palette.divider};
                      }

                      &:hover:not(.${pickersOutlinedInputClasses.error}):not(
                          .${pickersOutlinedInputClasses.focused}
                        )
                        .${pickersOutlinedInputClasses.notchedOutline} {
                        border-color: ${darken(theme.palette.divider, 0.15)};
                        border-width: 1px;
                      }

                      &&.${pickersOutlinedInputClasses.focused}
                        .${pickersOutlinedInputClasses.notchedOutline} {
                        border-width: 1px;
                      }

                      &&.${pickersInputBaseClasses.disabled} {
                        color: ${theme.palette.text.disabled};
                      }

                      ${
                        readOnly &&
                        greyReadOnly &&
                        css`
                          background-color: #eaeaea;
                          color: #636666;
                          font-weight: bold;
                          .${pickersInputBaseClasses.sectionsContainer} {
                            cursor: initial;
                          }
                        `
                      }
                    `,
                  },
                },
              }}
            />
          )}
        </ClassNames>
      </DateFieldProvider>
    );
  }
);
